import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { Fragment } from 'preact';
import {
  Plus, ArrowsClockwise, Wallet, Coins, ChartLineUp, CurrencyDollarSimple, BellRinging, BellSlash, Pencil,
  CaretDown,
} from '@phosphor-icons/react';
import { api, type SpotSnapshot, type SpotPosition } from '../../services/api.js';
import { useToast } from '../shared/Toast.js';
import { PositionCard, futuresDeliveryStatus } from './PositionCard.js';
import { PositionModal } from './PositionModal.js';
import { AllocationBar } from './AllocationBar.js';
import { AnimatedNumber } from '../shared/AnimatedNumber.js';
import { Sparkline } from '../shared/Sparkline.js';
import {
  formatSignedUsd,
  formatSignedRub,
  formatPct,
  formatUsdc,
  formatUsdPrice,
  formatQty,
} from './spotUtils.js';

const REFRESH_MS = 15_000;

interface InlineEdit {
  field: 'freeUsdc' | 'usdRubManual' | 'realizedUsd' | null;
  value: string;
}

export function SpotPage() {
  const [snapshot, setSnapshot] = useState<SpotSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ open: boolean; position: SpotPosition | null }>({ open: false, position: null });
  const [inlineEdit, setInlineEdit] = useState<InlineEdit>({ field: null, value: '' });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const inlineInputRef = useRef<HTMLInputElement>(null);
  const { show: toast } = useToast();

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const load = useCallback(async (silent = false): Promise<SpotSnapshot | null> => {
    try {
      if (!silent) setLoading(true);
      const data = await api.getSpot();
      setSnapshot(data);
      return data;
    } catch {
      if (!silent) toast('Не удалось загрузить спот-портфель', 'error');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load(false);
    const id = setInterval(() => { void load(true); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (inlineEdit.field) inlineInputRef.current?.focus();
  }, [inlineEdit.field]);

  const handleSaved = useCallback(() => {
    setModal({ open: false, position: null });
    void load(true);
  }, [load]);

  const handleDelete = useCallback(async (position: SpotPosition) => {
    const ok = window.confirm(`Удалить позицию ${position.name}?`);
    if (!ok) return;
    try {
      await api.deletePosition(position.id);
      toast('Позиция удалена', 'success');
      void load(true);
    } catch {
      toast('Не удалось удалить позицию', 'error');
    }
  }, [load, toast]);

  const handleResetTp = useCallback(async (position: SpotPosition) => {
    try {
      await api.resetPositionTp(position.id);
      toast('Цели сброшены — алерты могут сработать снова', 'success');
      void load(true);
    } catch {
      toast('Не удалось сбросить цели', 'error');
    }
  }, [load, toast]);

  const startEdit = (field: 'freeUsdc' | 'usdRubManual' | 'realizedUsd', currentValue: number | null) => {
    setInlineEdit({ field, value: currentValue !== null ? String(currentValue) : '' });
  };

  const commitInlineEdit = useCallback(async () => {
    if (!snapshot || !inlineEdit.field) return;
    const raw = inlineEdit.value.trim();
    const field = inlineEdit.field;

    let payload: { freeUsdc?: number; usdRubManual?: number | null; realizedUsd?: number };
    if (field === 'freeUsdc') {
      const n = parseFloat(raw);
      if (raw !== '' && (!Number.isFinite(n) || n < 0)) {
        toast('Некорректная сумма USDC', 'error');
        return;
      }
      payload = { freeUsdc: raw === '' ? 0 : n };
    } else if (field === 'realizedUsd') {
      const n = parseFloat(raw);
      if (raw !== '' && !Number.isFinite(n)) {
        toast('Некорректное значение', 'error');
        return;
      }
      payload = { realizedUsd: raw === '' ? 0 : n };
    } else {
      if (raw === '') {
        payload = { usdRubManual: null };
      } else {
        const n = parseFloat(raw);
        if (!Number.isFinite(n) || n <= 30 || n >= 500) {
          toast('USD/RUB должен быть в диапазоне 30..500', 'error');
          return;
        }
        payload = { usdRubManual: n };
      }
    }

    try {
      await api.saveSpotConfig(payload);
      toast('Сохранено', 'success');
      setInlineEdit({ field: null, value: '' });
      void load(true);
    } catch {
      toast('Не удалось сохранить', 'error');
    }
  }, [snapshot, inlineEdit, toast, load]);

  const toggleTgAlerts = useCallback(async () => {
    if (!snapshot) return;
    try {
      await api.saveSpotConfig({ telegramSpotAlerts: !snapshot.config.telegramSpotAlerts });
      toast(snapshot.config.telegramSpotAlerts ? 'ТГ-алерты выключены' : 'ТГ-алерты включены', 'info');
      void load(true);
    } catch {
      toast('Не удалось переключить', 'error');
    }
  }, [snapshot, toast, load]);

  const renderHero = () => {
    if (!snapshot) return null;
    const { totals, config, market, futures } = snapshot;

    // totals cover ALL positions (spot + futures). Derive the spot-only slice.
    const spotValueUsd = totals.valueUsd - futures.valueUsd;
    const spotCostUsd  = totals.costUsd - futures.costUsd;
    const spotPnlUsd   = totals.pnlUsd - futures.pnlUsd;
    const spotPnlPct   = spotCostUsd > 0 ? (spotPnlUsd / spotCostUsd) * 100 : 0;
    const spotPnlRub   = spotPnlUsd * market.usdRub;
    const futPnlPct    = futures.costUsd > 0 ? (futures.pnlUsd / futures.costUsd) * 100 : 0;

    const hasPositions = snapshot.positions.length > 0;
    const hasFutures = futures.count > 0;
    const profit = totals.pnlUsd >= 0;
    const pnlClass = hasPositions ? (profit ? 'profit' : 'loss') : '';
    const spotClass = spotPnlUsd >= 0 ? 'profit' : 'loss';
    const futClass = futures.pnlUsd >= 0 ? 'profit' : 'loss';
    const realizedClass = config.realizedUsd ? (config.realizedUsd >= 0 ? 'profit' : 'loss') : '';

    return (
      <div class="spot-hero">
        <div class="spot-hero-card">
          <div class="spot-hero-label">
            <Wallet size={14} weight="duotone" />
            <span>Стоимость портфеля</span>
          </div>
          <AnimatedNumber
            class="spot-hero-value"
            value={totals.valueUsd}
            format={(n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          />
          <div class="spot-hero-sub">
            {totals.valueRub.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
          </div>
          {hasFutures && (
            <div class="spot-hero-sub">
              спот ${spotValueUsd.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              {' · '}фьюч ${futures.valueUsd.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </div>
          )}
        </div>

        <div
          class={`spot-hero-card ${pnlClass}`}
          onDblClick={() => startEdit('realizedUsd', config.realizedUsd || null)}
          title="Двойной клик — указать зафиксированную прибыль"
        >
          <div class="spot-hero-label">
            <ChartLineUp size={14} weight="duotone" />
            <span>Итог P&amp;L</span>
          </div>
          <AnimatedNumber
            class={`spot-hero-value ${pnlClass}`}
            value={totals.pnlUsd}
            format={formatSignedUsd}
          />
          <div class={`spot-hero-sub ${hasPositions ? spotClass : ''}`}>
            спот {formatSignedUsd(spotPnlUsd)} · {formatSignedRub(spotPnlRub)} · {formatPct(spotPnlPct)}
          </div>
          <div class={`spot-hero-sub ${hasFutures ? futClass : ''}`}>
            фьючерсы {hasFutures ? `${formatSignedUsd(futures.pnlUsd)} · ${formatSignedRub(futures.pnlRub)} · ${formatPct(futPnlPct)}` : '—'}
          </div>
          {inlineEdit.field === 'realizedUsd' ? (
            <input
              ref={inlineInputRef}
              class="spot-hero-inline-input"
              inputMode="decimal"
              placeholder="Зафиксировано $"
              value={inlineEdit.value}
              onInput={(e) => setInlineEdit((s) => ({ ...s, value: (e.target as HTMLInputElement).value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitInlineEdit();
                else if (e.key === 'Escape') setInlineEdit({ field: null, value: '' });
              }}
              onBlur={commitInlineEdit}
            />
          ) : (
            <div class={`spot-hero-sub ${realizedClass}`}>
              зафиксировано {config.realizedUsd ? `${formatSignedUsd(config.realizedUsd)} · ${formatSignedRub(config.realizedUsd * market.usdRub)}` : '—'}
            </div>
          )}
        </div>

        <div class="spot-hero-card">
          <div class="spot-hero-label">
            <Coins size={14} weight="duotone" />
            <span>Свободно для DCA</span>
          </div>
          {inlineEdit.field === 'freeUsdc' ? (
            <input
              ref={inlineInputRef}
              class="spot-hero-inline-input"
              inputMode="decimal"
              value={inlineEdit.value}
              onInput={(e) => setInlineEdit((s) => ({ ...s, value: (e.target as HTMLInputElement).value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitInlineEdit();
                else if (e.key === 'Escape') setInlineEdit({ field: null, value: '' });
              }}
              onBlur={commitInlineEdit}
            />
          ) : (
            <div class="spot-hero-value">
              {formatUsdc(config.freeUsdc)}
            </div>
          )}
          <div class="spot-hero-sub">для дозакупки</div>
          {inlineEdit.field !== 'freeUsdc' && (
            <button class="spot-hero-edit" onClick={() => startEdit('freeUsdc', config.freeUsdc)} aria-label="Edit USDC">
              <Pencil size={12} />
            </button>
          )}
        </div>

        <div class="spot-hero-card">
          <div class="spot-hero-label">
            <CurrencyDollarSimple size={14} weight="duotone" />
            <span>Курс USD / RUB</span>
          </div>
          {inlineEdit.field === 'usdRubManual' ? (
            <input
              ref={inlineInputRef}
              class="spot-hero-inline-input"
              inputMode="decimal"
              placeholder="Пусто = ЦБ"
              value={inlineEdit.value}
              onInput={(e) => setInlineEdit((s) => ({ ...s, value: (e.target as HTMLInputElement).value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitInlineEdit();
                else if (e.key === 'Escape') setInlineEdit({ field: null, value: '' });
              }}
              onBlur={commitInlineEdit}
            />
          ) : (
            <div class="spot-hero-value">
              {market.usdRub.toFixed(2)} ₽
            </div>
          )}
          <span class={`spot-source-tag ${market.usdRubSource === 'manual' ? 'manual' : ''}`}>
            {market.usdRubSource === 'manual' ? 'ВРУЧНУЮ' : 'ЦБ РФ'}
          </span>
          {inlineEdit.field !== 'usdRubManual' && (
            <button
              class="spot-hero-edit"
              onClick={() => startEdit('usdRubManual', config.usdRubManual)}
              aria-label="Edit rate"
              title={market.usdRubSource === 'manual' ? 'Очистить — вернуться к ЦБ' : 'Задать вручную'}
            >
              <Pencil size={12} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div class="spot-toolbar">
        <div class="spot-toolbar-info">
          <span class="live-dot" />
          <span>
            {snapshot?.positions.length ?? 0} позиций
            {snapshot && ` · обновлено ${new Date(snapshot.market.updatedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            class="btn btn-secondary"
            onClick={toggleTgAlerts}
            disabled={!snapshot}
            title={snapshot?.config.telegramSpotAlerts ? 'ТГ-алерты включены' : 'ТГ-алерты выключены'}
          >
            {snapshot?.config.telegramSpotAlerts ? <BellRinging size={14} /> : <BellSlash size={14} />}
            <span>{snapshot?.config.telegramSpotAlerts ? 'ТГ ВКЛ' : 'ТГ ВЫКЛ'}</span>
          </button>
          <button class="btn btn-secondary" onClick={() => void load(false)}>
            <ArrowsClockwise size={14} />
            <span>Обновить</span>
          </button>
          <button class="btn btn-primary" onClick={() => setModal({ open: true, position: null })}>
            <Plus size={14} />
            <span>Добавить позицию</span>
          </button>
        </div>
      </div>

      {renderHero()}

      {loading && !snapshot ? (
        <div class="empty-state">
          <div class="empty-state-message">Загрузка спота…</div>
        </div>
      ) : snapshot && snapshot.positions.length === 0 ? (
        <div class="spot-empty">
          <div class="spot-empty-icon"><Coins size={24} weight="duotone" /></div>
          <div class="spot-empty-title">Спот портфель пуст</div>
          <div class="spot-empty-msg">
            Добавь первую позицию: выбери монету, количество, потраченную сумму и цели фиксации.
          </div>
          <div style={{ marginTop: 'var(--space-3)' }}>
            <button class="btn btn-primary" onClick={() => setModal({ open: true, position: null })}>
              <Plus size={14} />
              <span>Добавить позицию</span>
            </button>
          </div>
        </div>
      ) : (
        <>
        <AllocationBar positions={snapshot?.positions ?? []} totalValueUsd={snapshot?.totals.valueUsd ?? 0} />
        <div class="spot-table-wrap">
          <table class="data-table spot-table">
            <thead>
              <tr>
                <th class="col-coin">Монета</th>
                <th class="col-num">Кол-во</th>
                <th class="col-num">Средняя</th>
                <th class="col-num">Текущая</th>
                <th class="col-spark">7д</th>
                <th class="col-num">Стоимость</th>
                <th class="col-num">P&amp;L</th>
                <th class="col-num">%</th>
                <th class="col-num">24ч</th>
                <th class="col-tp">TP</th>
                <th class="col-dca">Докуп.</th>
                <th class="col-exp" />
              </tr>
            </thead>
            <tbody>
              {snapshot?.positions
                .slice()
                .sort((a, b) => {
                  // Спот сверху, фьючерсы — ниже; закрытые — в самый низ; внутри по стоимости.
                  if (a.isFutures !== b.isFutures) return a.isFutures ? 1 : -1;
                  const aClosed = a.closedAt != null;
                  const bClosed = b.closedAt != null;
                  if (aClosed !== bClosed) return aClosed ? 1 : -1;
                  const valueDiff = (b.valueUsd || 0) - (a.valueUsd || 0);
                  if (valueDiff !== 0) return valueDiff;
                  return a.symbol.localeCompare(b.symbol);
                })
                .map((p) => {
                  const isProfit = p.pnlUsd >= 0;
                  const isExpanded = expandedId === p.id;
                  const isClosed = p.closedAt != null;
                  const change24h = p.change24h ?? null;
                  const buysCount = (p.stages ?? []).length;
                  const tpReached = (p.triggered?.tp1 ? 1 : 0) + (p.triggered?.tp2 ? 1 : 0) + (p.triggered?.tp3 ? 1 : 0);
                  const closeStat = p.isFutures ? futuresDeliveryStatus(p) : null;
                  return (
                    <Fragment key={p.id}>
                      <tr
                        class={`spot-row clickable ${isExpanded ? 'expanded' : ''} ${isProfit ? 'profit' : 'loss'} ${isClosed ? 'closed' : ''}`}
                        onClick={() => toggleExpand(p.id)}
                      >
                        <td class="col-coin">
                          <div class="coin-cell">
                            {p.image
                              ? <img src={p.image} alt={p.symbol} class="coin-cell-logo" loading="lazy" />
                              : <span class="coin-cell-logo" />}
                            <div class="coin-cell-text">
                              <span class="coin-cell-name">
                                {p.symbol.toUpperCase()}
                                <span class={`spot-kind-badge ${p.isFutures ? 'fut' : 'spot'}`}>
                                  {p.isFutures ? 'FUTURE' : 'SPOT'}
                                </span>
                              </span>
                              <span class="coin-cell-sub">{p.name}</span>
                            </div>
                          </div>
                        </td>
                        <td class="col-num">{formatQty(p.qty)}</td>
                        <td class="col-num">{formatUsdPrice(p.avgPriceUsd)}</td>
                        <td class="col-num">
                          {formatUsdPrice(p.currentPriceUsd)}
                          {p.priceDivergencePct != null && (
                            <span
                              class="price-diverge-flag"
                              title={`Источники цены расходятся на ${p.priceDivergencePct}% (Bybit ↔ CoinGecko). Проверь символ/источник.`}
                            >⚠</span>
                          )}
                        </td>
                        <td class="col-spark">
                          {p.sparkline7d && p.sparkline7d.length > 1 ? (
                            <Sparkline
                              data={p.sparkline7d}
                              width={64}
                              height={20}
                              color={isProfit ? 'var(--pos)' : 'var(--neg)'}
                              fillColor={isProfit ? 'var(--pos-soft)' : 'var(--neg-soft)'}
                            />
                          ) : (
                            <span class="spot-spark-empty">—</span>
                          )}
                        </td>
                        <td class="col-num">${p.valueUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td class={`col-num ${isProfit ? 'profit' : 'loss'}`}>{formatSignedUsd(p.pnlUsd)}</td>
                        <td class={`col-num ${isProfit ? 'profit' : 'loss'}`}>{formatPct(p.pnlPct)}</td>
                        <td class={`col-num ${change24h !== null && change24h >= 0 ? 'profit' : 'loss'}`}>
                          {change24h !== null ? `${change24h >= 0 ? '+' : ''}${change24h.toFixed(2)}%` : '—'}
                        </td>
                        <td class="col-tp">
                          {p.isFutures ? (
                            isClosed ? (
                              <span
                                class={`tp-pill close-${closeStat?.kind ?? 'pending'}`}
                                title={closeStat?.label ?? 'Закрыта'}
                              >
                                {closeStat?.kind === 'ok' ? '✓' : closeStat?.kind === 'error' ? '⚠' : '⏳'} закрыта
                              </span>
                            ) : p.closePrice != null ? (
                              <span class="tp-pill armed" title={`Цель закрытия: ${formatUsdPrice(p.closePrice)}`}>
                                ⌖ {formatUsdPrice(p.closePrice)}
                              </span>
                            ) : (
                              <span class="tp-pill muted">—</span>
                            )
                          ) : (
                            <span class="tp-pill" title={`TP достигнуто: ${tpReached}/3`}>
                              {tpReached}<span class="tp-sep">/</span>3
                            </span>
                          )}
                        </td>
                        <td class="col-dca">
                          {p.isFutures ? (
                            <span class="dca-pill muted">—</span>
                          ) : (
                            <span class="dca-pill" title={`Покупок: ${buysCount}`}>
                              {buysCount}×
                            </span>
                          )}
                        </td>
                        <td class="col-exp">
                          <span class={`exp-caret ${isExpanded ? 'open' : ''}`}>
                            <CaretDown size={12} weight="bold" />
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr class="spot-row-detail">
                          <td colSpan={12}>
                            <PositionCard
                              position={p}
                              onEdit={(pos) => setModal({ open: true, position: pos })}
                              onDelete={handleDelete}
                              onResetTp={handleResetTp}
                              onChanged={() => void load(true)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {modal.open && (
        <PositionModal
          position={modal.position}
          onClose={() => setModal({ open: false, position: null })}
          onSaved={handleSaved}
        />
      )}

    </>
  );
}
